import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { StatusLamp } from './airport-ui';
import { toneClasses } from './airport-tokens';
import type { StatusBand } from './pnr-band';

// The passenger card's ONE status band (PO 2026-10-31): a section title, one sentence of
// where the PNR is, one line of why/what happens next, and ONE primary action (at most one
// secondary). Derived by `deriveBand` — it never contradicts the state. Presentational:
// the drawer renders the action buttons (they own mutations and dialogs).

export function PnrStatusBand({
  band,
  primary,
  secondary,
}: {
  band: StatusBand;
  primary?: ReactNode;
  secondary?: ReactNode;
}) {
  const tone = toneClasses(band.tone);
  const live = band.tone === 'warn' || band.tone === 'alert' || band.kind.startsWith('purchase-');
  return (
    <div
      className={cn(
        'mx-4 mt-4 flex shrink-0 flex-col gap-3 rounded-xl border px-4 py-3.5 sm:mx-6 sm:flex-row sm:items-center sm:gap-5',
        tone.surface,
        tone.border,
      )}
      role="status"
      aria-live="polite"
      aria-busy={band.loading ? true : undefined}
      data-testid="pnr-status-band"
      data-kind={band.kind}
      data-tone={band.tone}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className={cn('flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.06em]', tone.text)}>
          <StatusLamp tone={band.tone} pulse={live && band.tone !== 'ok'} className="h-2 w-2" />
          <span data-testid="pnr-band-kicker">{band.kicker}</span>
        </span>
        {band.loading ? (
          // Waiting on the read: a neutral line, never a claim it may contradict.
          <div className="flex flex-col gap-1.5" data-testid="pnr-band-loading">
            <p className="text-base text-muted-foreground" data-testid="pnr-band-title">
              {band.title}
            </p>
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : (
          <p className="text-lg font-bold leading-snug" data-testid="pnr-band-title">
            {band.title}
          </p>
        )}
        {band.body ? (
          <p className="text-sm text-muted-foreground" data-testid="pnr-band-body">
            {band.body}
          </p>
        ) : null}
      </div>
      {primary || secondary ? (
        <div className="flex shrink-0 flex-col gap-2 sm:min-w-[12rem]" data-testid="pnr-band-actions">
          {primary}
          {secondary}
        </div>
      ) : null}
    </div>
  );
}
